import {
  LayoutDashboard,
  Users,
  Building2,
  ShieldCheck,
  Package,
  CalendarCheck,
  CreditCard,
  LineChart,
  Activity,
  ScrollText,
  Settings as SettingsIcon,
  UserCircle,
  ClipboardList,
  Stethoscope,
  HelpCircle,
  FileCheck2,
  ScanLine,
  Gift,
  ListChecks,
  Mail,
  CalendarDays,
  Clock,
  Wallet,
  Landmark,
} from 'lucide-react';
import { ROLES } from '../utils/roles';

// Each menu is a flat list of sections; each section has items.
// item: { label, to, icon, badge? }
// Super-admin sidebar splits people into three distinct sections so they
// never get mixed: end users only, clinics with the approval workflow,
// and internal staff (super-admin / admin / staff).
const SUPER_ADMIN_MENU = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard',  to: '/dashboard',  icon: LayoutDashboard },
      { label: 'Analytics',  to: '/analytics',  icon: Activity }
    ]
  },
  {
    label: 'Users',
    items: [
      { label: 'End users', to: '/users', icon: Users }
    ]
  },
  {
    label: 'Clinics',
    items: [
      { label: 'Clinics & approvals', to: '/clinic-approvals', icon: Building2 }
    ]
  },
  {
    label: 'Internal staff',
    items: [
      { label: 'Admins / Staff', to: '/admins', icon: ShieldCheck }
    ]
  },
  {
    label: 'Operations',
    items: [
      { label: 'Treatment plans',   to: '/packages',          icon: Package },
      { label: 'Bookings',          to: '/orders',            icon: CalendarCheck },
      { label: 'Payments',          to: '/payments',          icon: CreditCard },
      { label: 'Clinic payouts',    to: '/admin/payouts',     icon: Wallet },
      { label: 'User progress',     to: '/reports',           icon: LineChart },
      { label: 'Quiz submissions',  to: '/quiz-submissions',  icon: ListChecks },
      { label: 'Assessments',       to: '/admin/assessments', icon: ClipboardList },
      { label: 'Referrals',         to: '/admin/referrals',   icon: Gift },
      { label: 'Early Access',      to: '/admin/waitlist',    icon: Mail }
    ]
  },
  {
    label: 'System',
    items: [
      { label: 'Audit logs', to: '/audit-logs', icon: ScrollText },
      { label: 'Settings',   to: '/settings',   icon: SettingsIcon }
    ]
  }
];

const ADMIN_STAFF_MENU = [
  {
    label: 'Workspace',
    items: [
      { label: 'Dashboard',        to: '/dashboard',        icon: LayoutDashboard },
      { label: 'Customers',        to: '/users',            icon: Users },
      { label: 'Bookings',         to: '/orders',           icon: CalendarCheck },
      { label: 'Clinics',          to: '/vendors',          icon: Building2 },
      { label: 'Clinic approvals', to: '/clinic-approvals', icon: FileCheck2 },
      { label: 'Quiz submissions', to: '/quiz-submissions', icon: ListChecks },
      { label: 'Assessments',      to: '/admin/assessments', icon: ClipboardList },
      { label: 'Referrals',        to: '/admin/referrals',  icon: Gift },
      { label: 'Early Access',     to: '/admin/waitlist',   icon: Mail },
      { label: 'Payments',         to: '/payments',         icon: CreditCard },
      { label: 'Clinic payouts',   to: '/admin/payouts',    icon: Wallet }
    ]
  },
  {
    label: 'Account',
    items: [
      { label: 'Profile',    to: '/profile',   icon: UserCircle },
      { label: 'Settings',   to: '/settings',  icon: SettingsIcon }
    ]
  }
];

// Clinic (merchant) workspace — formerly "vendor" / lab
const VENDOR_MENU = [
  {
    label: 'Clinic',
    items: [
      { label: 'Dashboard',         to: '/dashboard',              icon: LayoutDashboard },
      { label: 'Calendar',          to: '/clinic/calendar',        icon: CalendarDays },
      { label: 'Clinic profile',    to: '/clinic-profile',         icon: FileCheck2 },
      { label: 'Check-in',          to: '/check-in',               icon: ScanLine },
      { label: 'Bookings',          to: '/orders',                 icon: ClipboardList },
      { label: 'User progress',     to: '/reports',                icon: LineChart },
      { label: 'Booking settings',  to: '/clinic/booking-settings', icon: Clock },
    ]
  },
  {
    label: 'Finance',
    items: [
      { label: 'Payouts',       to: '/my-payouts',         icon: Wallet },
      { label: 'Bank details',  to: '/clinic/bank-details', icon: Landmark },
    ]
  },
  {
    label: 'Account',
    items: [
      { label: 'Profile',       to: '/profile',       icon: UserCircle },
      { label: 'Settings',      to: '/settings',      icon: SettingsIcon }
    ]
  }
];

// Customer workspace
const USER_MENU = [
  {
    label: 'My program',
    items: [
      { label: 'Dashboard',         to: '/dashboard', icon: LayoutDashboard },
      { label: 'Treatment plans',   to: '/packages',  icon: Package },
      // Client 2026-08-04: "Find a clinic" hidden from the USER sidebar.
      // Patients still reach the clinic list through the in-flow CTA on
      // their PAID order ("Pick a clinic") — direct browse is off.
      // { label: 'Find a clinic',     to: '/clinics',     icon: Building2 },
      { label: 'My bookings',       to: '/orders',      icon: CalendarCheck },
      { label: 'My progress',       to: '/my-progress', icon: Stethoscope },
      { label: 'Invite friends',    to: '/referrals',   icon: Gift },
      { label: 'Payments',          to: '/payments',    icon: CreditCard }
    ]
  },
  {
    label: 'Account',
    items: [
      { label: 'Profile',         to: '/profile',     icon: UserCircle },
      { label: 'Settings',      to: '/settings',      icon: SettingsIcon }
    ]
  }
];

export function menuFor(role) {
  switch (role) {
    case ROLES.SUPER_ADMIN: return SUPER_ADMIN_MENU;
    case ROLES.ADMIN:
    case ROLES.STAFF:       return ADMIN_STAFF_MENU;
    case ROLES.VENDOR:      return VENDOR_MENU;
    case ROLES.USER:
    default:                return USER_MENU;
  }
}

// Build a flat list of allowed paths for fast access checks in routes.
export function allowedPathsFor(role) {
  return new Set(
    menuFor(role)
      .flatMap((s) => s.items)
      .map((i) => i.to)
  );
}
